import { FC, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import {
  Button,
  Dialog,
  FormHelperText,
  IconArrowLeft,
  IconTrash,
  Input,
  Loader,
  Tooltip,
  Typography,
  Wrapper
} from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import Countdown from 'components/countdown/Countdown';
import ExternalLink, { ExternalLinkContentType } from 'components/external-link/ExternalLink';
import { ScheduleDialog } from 'components/schedule-dialog/ScheduleDialog';
import { useAlert } from 'contexts/alert/useAlert';
import { MobileIssueDtoArtifactFragment, useDeleteMobileIssueMutation } from 'data/generated/graphql';
import { useIsAdmin } from 'hooks/roles';
import styles from './IssueHeader.module.scss';
import { useIssueScheduling } from '../hooks/useScheduling';

type IssueHeaderProps = {
  issue: MobileIssueDtoArtifactFragment | null;
  onPublish: () => void;
  isPublishing: boolean;
  disabled: boolean;
};

export const IssueHeader: FC<IssueHeaderProps> = ({ issue, onPublish, isPublishing, disabled }) => {
  const { property } = useParams<{ property: string }>();
  const issueId = issue?.metadata.allessehId;
  const issuesListPath = `/${property ?? ''}/site-config/mobile-and-app/mobile-issues`;
  const { currentProperty } = useAuth();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { alertError } = useAlert();

  const { isScheduling, soonToBePublishedScheduledContentPublishUtc, handleSchedule } = useIssueScheduling({ issue });

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

  const { mutateAsync: deleteMobileIssue, isLoading: isDeleting } = useDeleteMobileIssueMutation();

  const toggleScheduleDialog = () => setShowScheduleModal((prev) => !prev);
  const closeScheduleDialog = () => setShowScheduleModal(false);

  const onScheduleFromDialog = async (publishUtc: number) => {
    const ok = await handleSchedule(publishUtc);
    if (ok) closeScheduleDialog();
  };

  const handleDelete = async () => {
    if (!issueId || !currentProperty) return;
    try {
      await deleteMobileIssue({ deleteMobileIssueInput: { publicationKey: currentProperty, allessehId: issueId } });
      await queryClient.invalidateQueries(['PublicationSettings']);
      setShowDeleteModal(false);
      navigate(issuesListPath);
    } catch (error) {
      alertError(error instanceof Error ? error.message : 'Failed to delete mobile issue.');
      setShowDeleteModal(false);
    }
  };

  if (!issueId) {
    return null;
  }

  return (
    <Wrapper>
      <Wrapper padding={{ top: 'md', bottom: 'md' }} className={styles.titleHeader}>
        <Wrapper className={styles.titleHeaderLeft}>
          <Tooltip>
            <Tooltip.Content className={styles.titleHeaderLeftTooltip} position="right">
              Go to Issue Selection Page
            </Tooltip.Content>
            <Tooltip.Trigger>
              <Button
                tertiary
                color="blurple"
                icon={IconArrowLeft}
                componentEl={Link}
                to={issuesListPath}
                disabled={isScheduling}
              />
            </Tooltip.Trigger>
          </Tooltip>

          <Typography variant="header2" margin={{ bottom: 'none' }}>
            {issueId}
          </Typography>
        </Wrapper>

        <Wrapper>
          <ExternalLink contentId={issueId} type={ExternalLinkContentType.COLLECTION} isPublished debug={false} />

          {isAdmin && (
            <Button
              color="lava"
              data-testid="mobile-issue-delete-button"
              icon={IconTrash}
              margin={{ left: 'sm' }}
              onClick={() => { setDeleteConfirmInput(''); setShowDeleteModal(true); }}
              tertiary
              disabled={isDeleting || isPublishing || isScheduling}
            >
              Delete
            </Button>
          )}

          <Button margin={{ left: 'sm' }} secondary onClick={toggleScheduleDialog} data-testid="issue-schedule-button">
            {isScheduling && <Loader size="md" margin={{ right: 'md' }} />}
            Schedule
          </Button>

          <Button primary margin={{ left: 'sm' }} onClick={onPublish} disabled={isPublishing || disabled}>
            {isPublishing && <Loader size="md" margin={{ right: 'md' }} />}
            Publish
          </Button>
        </Wrapper>

        {showScheduleModal && (
          <ScheduleDialog
            dialogTitle="Schedule Issue"
            onCancel={closeScheduleDialog}
            onSchedule={onScheduleFromDialog}
            isLoading={isScheduling}
          />
        )}
      </Wrapper>

      {soonToBePublishedScheduledContentPublishUtc && (
        <Wrapper className={styles.titleHeaderCountdown}>
          <Countdown
            timeInMilliseconds={soonToBePublishedScheduledContentPublishUtc}
            timerMessage="Next Scheduled Publish in"
            atZeroMessage="Please refresh the page to see the published issue. It may take a minute or so."
          />
        </Wrapper>
      )}

      {showDeleteModal && (
        <Dialog status="open" onDismiss={() => !isDeleting && setShowDeleteModal(false)}>
          <Dialog.Title id="deleteIssueTitle">Delete issue</Dialog.Title>
          <Dialog.Content id="deleteIssueContent">
            <Typography size="md" margin={{ bottom: 'sm' }}>
              Type the issue ID to confirm deletion:
            </Typography>
            <Typography weight="bold" margin={{ bottom: 'sm' }}>{issueId}</Typography>
            <Input
              aria-label="Confirm issue ID"
              value={deleteConfirmInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeleteConfirmInput(e.target.value)}
              placeholder={issueId}
              data-testid="delete-issue-confirm-input"
            />
            {deleteConfirmInput && deleteConfirmInput !== issueId && (
              <FormHelperText error margin={{ top: 'xs' }}>ID does not match.</FormHelperText>
            )}
          </Dialog.Content>
          <Dialog.Actions>
            <Button secondary onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>Cancel</Button>
            <Button
              primary
              color="lava"
              onClick={handleDelete}
              disabled={isDeleting || deleteConfirmInput !== issueId}
              data-testid="delete-issue-confirm-button"
            >
              {isDeleting ? <Loader size="md" /> : 'Delete'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      )}
    </Wrapper>
  );
};
